import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Icon } from "../../components/ui/Icon.js";
import { LATEST_RELEASE_ID, RELEASES, WHATS_NEW_STORAGE_KEY } from "./releases.js";

function readSeen(): string | null {
  try {
    return localStorage.getItem(WHATS_NEW_STORAGE_KEY);
  } catch {
    // Sin almacenamiento (navegación privada estricta): no insistir en cada carga.
    return LATEST_RELEASE_ID;
  }
}

function markSeen() {
  try {
    localStorage.setItem(WHATS_NEW_STORAGE_KEY, LATEST_RELEASE_ID);
  } catch {
    // Sin almacenamiento disponible: se volverá a mostrar en la próxima carga.
  }
}

/** Estado del aviso: se abre solo si la última actualización no se ha mostrado en este navegador. */
export function useWhatsNew() {
  const [open, setOpen] = useState(() => readSeen() !== LATEST_RELEASE_ID);
  const close = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);
  return { open, show: () => setOpen(true), close };
}

/** Aviso grande de novedades de la última actualización. */
export function WhatsNewDialog({ open, onClose, onTour }: { open: boolean; onClose: () => void; onTour?: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const release = RELEASES[0]!;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      className="m-auto w-[min(94vw,46rem)] overflow-hidden rounded-3xl p-0 shadow-2xl backdrop:bg-ink/60"
      data-testid="whats-new"
    >
      {open && (
        <div className="flex max-h-[88vh] flex-col">
          <header className="relative overflow-hidden bg-ink px-7 py-6 text-white">
            <img src="/brand/damateur-icon-512.png" alt="" className="pointer-events-none absolute -right-10 -top-8 w-56 opacity-10" />
            <p className="font-util text-xs uppercase tracking-[0.3em] text-aqua">Novedades · {release.date}</p>
            <h2 id={titleId} className="mt-2 font-display text-3xl font-extrabold leading-tight">
              {release.title}
            </h2>
          </header>
          <ul className="flex-1 space-y-4 overflow-y-auto px-7 py-6">
            {release.items.map((item) => (
              <li key={item.title} className="flex gap-4">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-800">
                  <Icon name={item.icon} />
                </span>
                <div>
                  <p className="font-semibold text-ink">{item.title}</p>
                  <p className="mt-0.5 text-sm text-slate-600">{item.text}</p>
                </div>
              </li>
            ))}
          </ul>
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-7 py-4">
            {onTour && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  onClose();
                  onTour();
                }}
              >
                <Icon name="help" className="h-4 w-4" /> Ver el recorrido de esta pantalla
              </button>
            )}
            <button type="button" className="btn-primary" onClick={onClose} autoFocus data-testid="whats-new-close">
              Entendido
            </button>
          </footer>
        </div>
      )}
    </dialog>
  );
}
